import { DeliverrAddress } from "@deliverr/commons-objects";
import { DistributionChannel, LineItemRequest, OrderStatus } from "@deliverr/replenishment-client";
import { ATPDetails } from "common/clients/inventory/ATP/ATPDetails";
import { SelectedProductData } from "common/components/ProductChooser";
import { entries } from "lodash";
import { ReplenishmentConstants } from "transfers/constants/ReplenishmentConstants";

/**
 * Normalizes a DeliverrAddress object by ensuring all fields have default values.
 * @param destinationAddress - The input address object to normalize.
 * @returns A normalized DeliverrAddress object with default values for missing fields.
 */
export const normalizeDestinationAddress = (destinationAddress: Partial<DeliverrAddress> = {}): DeliverrAddress => {
  return {
    name: destinationAddress.name ?? "",
    company: destinationAddress.company ?? "",
    street1: destinationAddress.street1 ?? "",
    street2: destinationAddress.street2 ?? "",
    city: destinationAddress.city ?? "",
    state: destinationAddress.state ?? "",
    country: destinationAddress.country ?? "",
    zip: destinationAddress.zip ?? "",
    phone: destinationAddress.phone ?? "",
    email: destinationAddress.email ?? "",
    isResidential: destinationAddress.isResidential ?? false,
  };
};

/**
 * Builds line items for the replenishment order.
 * @param selectedProducts - The selected products for the order.
 * @param selectedProductsLotNumbers - The lot numbers for the selected products.
 * @param isEachPickingOrder - Whether the order is for each-picking.
 * @returns An array of LineItemRequest objects.
 */
export const buildLineItems = (
  selectedProducts: { [dsku: string]: SelectedProductData },
  selectedProductsLotNumbers: { [dsku: string]: ATPDetails | undefined }
): LineItemRequest[] => {
  // ToDO - Add expiration date to line items
  return entries(selectedProducts).map(([, product]) => {
    const lotNumber = selectedProductsLotNumbers[product.dsku]?.lotNumber ?? undefined;
    return {
      identifiers: {
        dsku: product.dsku,
      },
      totalUnits: product.qty ?? 0,
      unitsPerPack: product.caseQty ?? 1,
      lotNumber,
      lineItemId: product.lineItemId ?? product.dsku,
    };
  });
};

/**
 * Maps CREATED status to DRAFT for non-WHOLESALE and non-FBA_MANUAL channels,
 * to support migration to the new flow.
 *
 * @param orderStatus - The original order status.
 * @param distributionChannel - The distribution channel for the order.
 * @returns The adjusted order status.
 */
export const getAdjustedOrderStatus = (
  orderStatus: OrderStatus,
  distributionChannel: DistributionChannel
): OrderStatus => {
  if (
    orderStatus === OrderStatus.CREATED &&
    ![DistributionChannel.WHOLESALE, DistributionChannel.FBA_MANUAL, ...ReplenishmentConstants.ECOM_CHANNELS].includes(
      distributionChannel
    )
  ) {
    return OrderStatus.DRAFT;
  }
  return orderStatus;
};

export const isIntegratedFBAReplenishmentOrder = (distributionChannel: DistributionChannel) => {
  return [DistributionChannel.FBA, DistributionChannel.FBA_V3].includes(distributionChannel);
};
